import {Grid, Theme, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import moment from 'moment';
import RoomParty from './RoomParty';
import {Group} from '../../../graphql/groups.datatypes';
import {BookingData, newRoomData, RoomData} from '../helperFunctions';
import {CtaSettingsType} from '../BookingTool';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(0),
        paddingBottom: theme.spacing(3),

        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(3),
        },
    },
    padBottom: {
        paddingBottom: theme.spacing(3),
    },
    heading: {
        fontFamily: 'Recoleta-SemiBold',
    },
    body: {
        fontFamily: 'Nunito',
        fontSize: 18,
    }
}));

interface Props {
    setActiveStep: (num: number) => void;
    group: Group;
    bookingData: BookingData;
    setBookingData: (data: BookingData) => void;
    setCtaSettings: (ctaSettings: CtaSettingsType | null) => void;
    isExistingBooking?: boolean;
    readonly?: boolean;
}

const Step2GuestDetails = ({group, bookingData, setBookingData, setCtaSettings, isExistingBooking, readonly}: Props) => {
    const classes = useStyles();
    const [billingPartyId, setBillingPartyId] = useState('');
    const containerRef = useRef<HTMLDivElement | null>(null);

    const checkCapacityAvailable = (numGuests: number = 1, checkNewRoom: boolean = false) => {
        let capacity = false;
        if (group && group.hotel && group.hotel.roomResources) {
            const rooms = group.hotel.roomResources
                .filter(roomResource => roomResource.guest_capacity! >= numGuests)
                .sort((a, b) => a.guest_capacity! - b.guest_capacity!)
                .filter(roomResource => roomResource.resource_banks!
                        .filter(bank => {
                            const date = moment(bank.date);
                            return (
                                date.isSameOrAfter(group.start_date) &&
                                date.isBefore(group.end_date)
                            );
                        })
                        .reduce((accum: boolean, bank) => {
                            if (!accum) return false;
                            //check remaining rooms are greater or equal to the number of rooms in the new booking with the capacity of this room or less.
                            //Need to not count rooms in booking with guests that fit in smaller capacity available rooms.
                            return (bank.remaining! >= bookingData.roomsData.filter(roomData => roomData.guestsData.length < roomResource.guest_capacity!).length + (checkNewRoom ? 1 : 0))
                        }, true)
                );

            capacity = rooms.length > 0
        }

        return capacity;
    };

    const getAvailableMaxCapacity = () => {
        let capacity = 0;
        if (group && group.hotel && group.hotel.roomResources) {
            const largestRoom = [...group.hotel.roomResources]
                .sort((a, b) => b.guest_capacity! - a.guest_capacity!)
                .shift();

            if(largestRoom) capacity = largestRoom.guest_capacity!;

            while(capacity > 0){
                if(checkCapacityAvailable(capacity, true)) break;
                capacity-=1;
            }
        }

        return capacity;
    };

    const getCurrentMaxCapacity = () => {
        let capacity = 0;
        if (group && group.hotel && group.hotel.roomResources) {
            const largestRoom = [...group.hotel.roomResources]
                .sort((a, b) => b.guest_capacity! - a.guest_capacity!)
                .shift();

            if(largestRoom) capacity = largestRoom.guest_capacity!;

            while(capacity > 0){
                if(checkCapacityAvailable(capacity, false)) break;
                capacity-=1;
            }
        }

        return capacity;
    };

    const addRoom = () => {
        const roomsData: RoomData[] = [...bookingData.roomsData, newRoomData(billingPartyId, group.hotel!.internal_name)];
        const isValid = roomsData.find(data => !data.isValid) === undefined;
        const validData = bookingData.validData.map((data, i) => i === 2 ? isValid : data);
        setBookingData({...bookingData, roomsData, validData});
    };

    const removeRoom = (targetData: RoomData) => {
        const roomsData: RoomData[] = bookingData.roomsData.filter(roomData => roomData.id !== targetData.id);
        const isValid = roomsData.find(data => !data.isValid) === undefined;
        const validData = bookingData.validData.map((data, i) => i === 2 ? isValid : data);
        setBookingData({...bookingData, roomsData, validData});
    };

    const setRoomData = (targetData: RoomData) => {
        const roomsData: RoomData[] = bookingData.roomsData.map(roomData => roomData.id !== targetData.id ? roomData : targetData);
        const isValid = roomsData.find(data => !data.isValid) === undefined;
        const validData = bookingData.validData.map((data, i) => i === 2 ? isValid : data);
        setBookingData({...bookingData, roomsData, validData});
    };

    useEffect(() => {
        const billingParty = bookingData.billingPartiesData.find(partyData => partyData.onlyParty);
        const newId = billingParty ? billingParty.id : '';
        if (billingPartyId !== newId) setBillingPartyId(newId);
    }, [bookingData]);

    useEffect(() => {
        const canAddRoom = checkCapacityAvailable(1, true);
        setCtaSettings({handler: addRoom, label: '+ Add Room', disabled: readonly || !canAddRoom});

        return () => {
            setCtaSettings(null);
        };
    }, [bookingData]);

    return (
        <div className={classes.root} ref={containerRef}>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Typography
                        variant="h1"
                        className={clsx([classes.heading, classes.padBottom])}
                    >
                        Travelers
                    </Typography>

                    {isExistingBooking && (
                        <Typography
                            paragraph
                            style={{color: '#E60000'}}
                        >
                            To add a Traveler, cancel a Traveler or do a name change of an existing Traveler please use "Contact Us" to make your request.
                        </Typography>
                    )}
                </Grid>
            </Grid>
            {bookingData.roomsData.map((roomData, i) => (
                <RoomParty
                    key={i}
                    checkCapacityAvailable={checkCapacityAvailable}
                    getAvailableMaxCapacity={getAvailableMaxCapacity}
                    getCurrentMaxCapacity={getCurrentMaxCapacity}
                    group={group}
                    roomData={roomData}
                    addRoom={addRoom}
                    removeRoom={removeRoom}
                    setRoomData={setRoomData}
                    billingPartyId={billingPartyId}
                    bookingData={bookingData}
                    containerRef={containerRef}
                    readonly={readonly}
                />
            ))}
        </div>
    );
};

export default Step2GuestDetails;
